import { useCallback, useEffect, useLayoutEffect, useRef } from 'react';

import { fg } from '@atlaskit/platform-feature-flags';
import type { RovoApp } from '@atlassian/conversation-assistant-chat-prompt-input/controllers/chat-mode-store/types';
import {
	type AssistanceService,
	isSSEMessage,
	isStreamAgenticUi,
	isStreamAnswerPart,
	type StreamAgenticUi,
} from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import { useAssistanceService } from '@atlassian/conversation-assistant-service/services/assistance-service/main';
import { useChatMessagesActions } from '@atlassian/conversation-assistant-store/controllers/chat/use-chat-messages';

import { addAgenticUiMessage } from '../../controllers/chat-store/addAgenticUiMessage';
import { appendTextToAssistantMessage } from '../../controllers/chat-store/appendTextToAssistantMessage';

export type A2UIActionPayload = {
	eventName: string;
	context?: Record<string, unknown>;
	dataModel?: Record<string, unknown>;
};

type UseA2UIActionParams = {
	conversationId: string;
	agentId?: string;
	webSearchEnabled?: boolean;
	useCurrentBrowsingContext?: boolean;
	appFilters?: RovoApp[];
};

export type UseA2UIActionReturn = {
	handleA2UIAction: (action: A2UIActionPayload, assistantMessageId: string) => Promise<void>;
};

type SendMessageStreamMessage = Parameters<AssistanceService['sendMessageStream']>[0]['message'];
type ChatMessagesActions = ReturnType<typeof useChatMessagesActions>;
type A2UIActionState = UseA2UIActionParams & {
	assistanceService: AssistanceService;
	getMessages: ChatMessagesActions['getMessages'];
	setMessages: ChatMessagesActions['setMessages'];
};

const useIsomorphicLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect;

const consumeA2UIActionStream = async ({
	generator,
	onAgenticUi,
	onTextPart,
}: {
	generator: AsyncGenerator<unknown>;
	onAgenticUi: (streamMessage: StreamAgenticUi) => void;
	onTextPart: (text: string) => void;
}) => {
	while (true) {
		const { value, done } = await generator.next();

		if (done) {
			return;
		}

		if (isStreamAgenticUi(value)) {
			onAgenticUi({
				...value.data,
				type: 'AGENTIC_UI',
			});
		} else {
			// Unwrap SSE messages to check for text content (ANSWER_PART)
			const streamMessage = isSSEMessage(value) ? value.data : value;
			if (isStreamAnswerPart(streamMessage)) {
				onTextPart(streamMessage.message.content);
			}
		}
	}
};

const toStructuredActionContent = (action: A2UIActionPayload, assistantMessageId: string) => {
	const userActionContext = action.context ?? action.dataModel ?? {};

	return {
		data_parts_mime_type: 'application/json+a2ui',
		structured: true,
		data_parts: [
			{
				userAction: {
					actionName: action.eventName,
					context: {
						...userActionContext,
						assistantMessageId,
					},
				},
			},
		],
	};
};

export const useA2UIAction = ({
	conversationId,
	agentId,
	webSearchEnabled,
	useCurrentBrowsingContext,
	appFilters,
}: UseA2UIActionParams): UseA2UIActionReturn => {
	const assistanceService = useAssistanceService();
	const { getMessages, setMessages } = useChatMessagesActions();

	const latestActionStateRef = useRef<A2UIActionState | undefined>(undefined);

	useIsomorphicLayoutEffect(() => {
		latestActionStateRef.current = {
			conversationId,
			agentId,
			webSearchEnabled,
			useCurrentBrowsingContext,
			appFilters,
			assistanceService,
			getMessages,
			setMessages,
		};
	}, [
		agentId,
		appFilters,
		assistanceService,
		conversationId,
		getMessages,
		setMessages,
		useCurrentBrowsingContext,
		webSearchEnabled,
	]);

	const handleA2UIAction = useCallback(
		async (action: A2UIActionPayload, assistantMessageId: string) => {
			try {
				if (!fg('jira_ai_rendering_a2ui')) {
					return;
				}

				const latestActionState = latestActionStateRef.current;
				if (!latestActionState) {
					return;
				}

				const {
					conversationId,
					assistanceService,
					agentId,
					webSearchEnabled,
					useCurrentBrowsingContext,
					appFilters,
				} = latestActionState;

				if (!conversationId || !assistantMessageId) {
					return;
				}

				const getActiveActionState = () => {
					const activeActionState = latestActionStateRef.current;
					return activeActionState?.conversationId === conversationId
						? activeActionState
						: undefined;
				};

				const { generator } = await assistanceService.sendMessageStream({
					// eslint-disable-next-line @atlaskit/platform/no-double-type-assertions -- Existing A2UI baseline: structured action content is adapted to the assistance service stream message type.
					message: toStructuredActionContent(
						action,
						assistantMessageId,
					) as unknown as SendMessageStreamMessage,
					mimeType: 'text/json',
					agentId,
					conversationId,
					storeMessage: false,
					citationsEnabled: true,
					webSearchEnabled,
					useCurrentBrowsingContext,
					appFilters: appFilters?.map((app) => app.source),
					optInSSEStream: true,
				});

				await consumeA2UIActionStream({
					generator,
					onAgenticUi: (streamMessage) => {
						const activeActionState = getActiveActionState();
						if (!activeActionState) {
							return;
						}

						activeActionState.setMessages(
							conversationId,
							addAgenticUiMessage(
								activeActionState.getMessages(conversationId),
								assistantMessageId,
								streamMessage,
							),
						);
					},
					onTextPart: (text) => {
						const activeActionState = getActiveActionState();
						if (!activeActionState) {
							return;
						}

						activeActionState.setMessages(
							conversationId,
							appendTextToAssistantMessage(
								activeActionState.getMessages(conversationId),
								assistantMessageId,
								text,
							),
						);
					},
				});
			} catch {
				return;
			}
		},
		[],
	);

	return {
		handleA2UIAction: handleA2UIAction,
	};
};
